import Persistable from "../../../storage/persistable";
import TransitionBuilder from "../state/transition-builder";
import StateBuilder from "../state/state-builder";
import {
    getTransition,
    getTransitionFromStates,
    restore
} from "../../common/state/state";
import FragmentFactory from "../../../services/factories/fragment-factory";
import FragmentType from "../enums/fragment-type";
import NotImplemented from "../../../exceptions/not-implemented";

export default class Play extends Persistable {
    constructor(stateMachine) {
        super();
        this.fsm = stateMachine;
        this.fragments = [];
        this.historyFragments = [];
        this.gameId = null;
        this.lastSummary = false;
    }

    doTransition(transitionName, fragment, gameContext) {
        const transitionDefinition = this.getTransition(transitionName);
        let historyIndex = this.getHistory().length;
        if (fragment && fragment.associatedHistoryIndex) {
            historyIndex = fragment.associatedHistoryIndex;
        }
        const requiredFragment = FragmentFactory.build(
            this.id,
            {
                ...fragment,
                associatedState: transitionDefinition.to,
                associatedTransition: transitionName,
                associatedHistoryIndex: historyIndex
            },
            {
                sportCode: this.getSportCode(),
                collectTime: this.shouldCollectTime(gameContext?.gameConfig),
                ruleSetId: gameContext?.ruleSetId,
                play: this
            }
        );

        if (
            requiredFragment &&
            !requiredFragment.hasRequiredPartialData(this)
        ) {
            throw Error(
                `Missing fragment data. One of the following values are missing: ${requiredFragment.requiredData
                    .map((rd) => rd.name)
                    .join(", ")}`
            );
        }

        this.fsm.doTransition(transitionName);

        if (requiredFragment) {
            this.addOrUpdateFragment(requiredFragment);
        }

        return this;
    }

    getSportCode() {
        throw new Error("Not implemented");
    }

    shouldPromptSkipKickoff() {
        throw new NotImplemented();
    }

    shouldCollectTime() {
        throw new NotImplemented();
    }

    getState() {
        return this.fsm.state;
    }

    getAllowedTransitions() {
        return this.fsm.transitions().map((t) => {
            return TransitionBuilder.build(t);
        });
    }

    historyContains(state) {
        return this.fsm.history.includes(state);
    }

    getFirstFragment(state) {
        const [fragment] = this.getFragments(state);

        return fragment || this._rebuildMissingFragment(state);
    }

    /**
     * Every state a play reached owns a fragment. When one has gone missing
     * (an unwound play whose fragments were pruned and then re-entered)
     * rebuild it from its position in the state history so consumers get a
     * fragment instead of undefined -- reading one used to throw inside the
     * rules engine and left the game unusable. The rebuilt fragment is added
     * to the play, so the next savePlay persists the repair.
     * @param {string} state
     * @returns {Fragment | undefined} undefined for a state the play never
     * reached, or one that carries no fragment of its own.
     * @private
     */
    _rebuildMissingFragment(state) {
        if (!state || Array.isArray(state)) {
            return undefined;
        }

        const history = this.fsm?.history || [];
        const associatedHistoryIndex = history.indexOf(state);
        // Index 0 is the initial state, which precedes every fragment.
        if (associatedHistoryIndex < 1) {
            return undefined;
        }

        const transition = getTransitionFromStates(
            this.fsm,
            history[associatedHistoryIndex - 1],
            state
        );
        if (!transition) {
            return undefined;
        }

        const fragment = FragmentFactory.build(
            this.id,
            {
                associatedState: state,
                associatedTransition: transition.name,
                associatedHistoryIndex
            },
            { sportCode: this.getSportCode(), play: this }
        );

        if (fragment) {
            this.addOrUpdateFragment(fragment);
        }

        return fragment;
    }

    getLastFragment(state) {
        return this.getFragments(state).slice(-1)[0];
    }

    getFragments(state) {
        if (Array.isArray(state)) {
            return this.fragments.filter((f) =>
                state.includes(f.associatedState)
            );
        } else {
            return this.fragments.filter((f) => f.associatedState === state);
        }
    }

    getFragment(state, index) {
        return this.fragments.find(
            (f) =>
                state === f.associatedState &&
                index === f.associatedHistoryIndex
        );
    }

    getTransitionAfter(historyIndex) {
        const nextState = this.getStateAfter(historyIndex);
        if (nextState) {
            const nextFragment = this.getFragment(
                nextState.name,
                historyIndex + 1
            );
            if (nextFragment) {
                return nextFragment.associatedTransition;
            }
        } else {
            const pendingFragment = this.getPendingFragment();
            if (pendingFragment) {
                return pendingFragment.associatedTransition;
            }
        }

        return null;
    }

    addOrUpdateFragment(fragment) {
        const index = this.fragments.findIndex((f) => f.equals(fragment));
        if (index >= 0) {
            Object.assign(this.fragments[index], fragment);
        } else {
            this.fragments.push(fragment);
        }
    }

    archiveStateFragment(state) {
        if (this.fragments) {
            let i = this.fragments.length - 1;
            for (i; i >= 0; i--) {
                if (this.fragments[i].associatedState === state) {
                    this.historyFragments.push(this.fragments[i]);
                    this.fragments.splice(i, 1);
                    return;
                }
            }
        }
    }

    recoverStateFragment(state) {
        if (this.historyFragments) {
            let i = 0;
            for (i; i < this.historyFragments.length; i++) {
                if (this.historyFragments[i].associatedState === state) {
                    this.fragments.push(this.historyFragments[i]);
                    this.historyFragments.splice(i, 1);
                    return;
                }
            }
        }
    }

    canGoBack() {
        return this.fsm.canHistoryBack;
    }

    canGoForward() {
        return this.fsm.canHistoryForward;
    }

    goBack() {
        this.archiveStateFragment(this.getState());

        this.fsm.historyBack();

        return this;
    }

    goForward() {
        this.fsm.historyForward();

        this.recoverStateFragment(this.getState());

        return this;
    }

    goBackTo(historyIndex) {
        // Stop *at* the target instead of walking until the length matches it
        // exactly: a target at or beyond the current history can never be
        // matched, so the loop used to unwind the whole play down to its
        // initial state -- and the caller then pruned every fragment it had
        // just orphaned, including the StartOfPlay marker the rules engine
        // needs. An unreachable target is now a no-op.
        while (this.fsm.history.length > historyIndex + 1 && this.canGoBack()) {
            this.goBack();
        }
    }

    getTransitionHistory() {
        if (!this.fsm.history) {
            return [];
        }

        const transitions = [];
        for (let i = 0; i < this.fsm.history.length - 1; i++) {
            const fromState = this.fsm.history[i];
            const toState = this.fsm.history[i + 1];
            const transition = getTransitionFromStates(
                this.fsm,
                fromState,
                toState
            );

            if (transition) {
                const transitionEntity = TransitionBuilder.getByName(
                    transition.name
                );
                if (transitionEntity) {
                    transitions.push({
                        ...transitionEntity,
                        from: fromState,
                        to: toState
                    });
                }
            }
        }

        return transitions;
    }

    getTransition(transitionName) {
        return getTransition(this.fsm, transitionName);
    }

    getHistory() {
        if (!this.fsm.history) {
            return [];
        }

        // build() falls back to a bare State for history entries whose state
        // no longer exists (e.g. END_DRIVE_TIME, removed in PSTATS-842), so
        // legacy plays keep a dense, crash-safe history.
        return this.fsm.history.map((state) => StateBuilder.build(state));
    }

    getStateTransitions(state) {
        return this.fsm._fsm.config.transitionsFor(state).map((t) => {
            return TransitionBuilder.build(t);
        });
    }

    canTransitionTo(transitionName) {
        return this.fsm.can(transitionName);
    }

    getFuture() {
        if (!this.fsm.future) {
            return [];
        }

        return this.fsm.future.map((state) => StateBuilder.getByName(state));
    }

    getAllVisitedStates() {
        return [...this.getHistory(), ...this.getFuture()];
    }

    getPreviousTransition(transitionName) {
        const transitionHistory = this.getTransitionHistory();
        const index = this.getTransitionHistory().findIndex(
            (t) => t.name === transitionName
        );

        if (index > 0) {
            return transitionHistory[index - 1];
        }

        return null;
    }

    requiresDataForTransition(transitionName, gameContext) {
        const fragment = this.getNextFragmentForTransition(
            transitionName,
            gameContext
        );
        return fragment && fragment.requiresData();
    }

    getStateAfter(fromStateIndex) {
        let state;
        const history = this.getHistory();
        if (history.length > fromStateIndex) {
            state = history[fromStateIndex + 1];
        }

        return state;
    }

    getNextFragmentForTransition(transitionName, gameContext) {
        const transition = this.getTransition(transitionName);
        const stateName = StateBuilder.getByName(transition.to).name;
        return FragmentFactory.build(
            this.id,
            {
                associatedState: stateName,
                associatedTransition: transitionName,
                associatedHistoryIndex: 0
            },
            {
                sportCode: this.getSportCode(),
                collectTime: this.shouldCollectTime(gameContext?.gameConfig),
                ruleSetId: gameContext?.ruleSetId
            }
        );
    }

    getNextFragment(fragment) {
        let nextFragment;
        if (fragment && this.fragments && fragment.id) {
            const index = this.fragments.findIndex((f) => f.id === fragment.id);
            if (index >= 0 && index < this.fragments.length - 1) {
                nextFragment = this.fragments[index + 1];
            }
        }

        return nextFragment;
    }

    getPreviousFragment(fragment) {
        let previousFragment;
        if (fragment && this.fragments) {
            const index = this.fragments.findIndex((f) => f.equals(fragment));
            if (index > 0) {
                previousFragment = this.fragments[index - 1];
            }
        }

        return previousFragment;
    }

    getPreviousFragmentOfState(fragment, state) {
        let previousFragment;
        if (fragment && this.fragments) {
            const index = this.fragments.findIndex(
                (f) =>
                    f.associatedHistoryIndex ===
                        fragment.associatedHistoryIndex &&
                    f.associatedState === fragment.associatedState
            );
            if (index > 0) {
                let currentFragment;
                for (let i = index; i > 0 && !previousFragment; i--) {
                    currentFragment = this.fragments[i];
                    if (currentFragment.associatedState === state) {
                        previousFragment = currentFragment;
                    }
                }
            }
        }

        return previousFragment;
    }

    getPendingFragment() {
        const playHistoryLength = this.getHistory().length;

        return this.fragments.find(
            (f) => f.associatedHistoryIndex === playHistoryLength
        );
    }

    hasTransitionedAlready(fragment) {
        return fragment.associatedHistoryIndex < this.getHistory().length;
    }

    nextStateReqSatisfied(associatedState) {
        return !this.fragments.some((f) =>
            f.requiredData.some((rd) =>
                rd.requiredAfterNextState?.some(
                    (state) => state === associatedState && !f[rd.name]
                )
            )
        );
    }

    canEndTransition(fragment) {
        return (
            !this.hasTransitionedAlready(fragment) &&
            this.canTransitionTo(fragment.associatedTransition) &&
            // !this.getNextFragment(fragment) && // Not sure we need this condition anymore.
            fragment.hasRequiredPartialData(this)
        );
    }

    isUpdate() {
        return this.fragments.some(
            (f) => f.fragmentType === FragmentType.Update
        );
    }

    toJSON(
        includedProps = [],
        excludedProps = [
            "lastSummary",
            "fsm",
            "fragments",
            "historyFragments",
            "messages"
        ]
    ) {
        this._state = {
            initialState: this.fsm.state,
            history: this.fsm.history ? this.fsm.history.concat([]) : [],
            future: this.fsm.future ? this.fsm.future.concat([]) : []
        };
        return super.toJSON(includedProps, excludedProps);
    }

    fromJSON(obj) {
        this.fsm = restore(this.fsm, obj._state);
        delete this._state;
    }
}
